import { fetchWithDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import {
  ACTOR_SYNC_HEAD_HEADER,
  ACTOR_SYNC_PART_HEADER,
  ACTOR_SYNC_PROTOCOL,
  ACTOR_SYNC_PROTOCOL_HEADER,
  ACTOR_SYNC_REFUSAL_HEADER,
  ACTOR_SYNC_SET_ASIDE_HEADER,
  type ActorJournalHead,
  type ActorSyncGatewayRefusal,
  formatActorJournalHead,
} from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";

/**
 * The transport the sync engine uses on a device (see `database.md`, "Server: sync gateway").
 *
 * The engine speaks the stock Turso protocol and this changes none of it. It only sends every
 * request to the gateway instead of the cloud copy, and tells the gateway two things: which
 * protocol the app speaks, and which migrations the replica has. Everything that makes sync safe
 * (migrating the cloud copy first, applying an upload or setting it aside) happens in the gateway,
 * so it can be fixed without an app release.
 *
 * One thing it does to an upload: an upload too large for one request is sent as several, in
 * order. The engine never splits one local transaction, so a single large transaction (a restored
 * backup, a whole level enrolled at once) would otherwise be an upload no server accepts, sent
 * again forever. The split is mechanical: it cuts the engine's list of statements and understands
 * none of them. See {@link splitActorSyncUpload}.
 */

/** What the gateway said about one request. The sync supervisor decides what to do with it. */
export interface ActorSyncGatewayAnswer {
  /** Why the gateway answered the request itself; `null` when it served it. */
  refusal: ActorSyncGatewayRefusal | null;
  /** How many statements of an upload were set aside in the cloud instead of applied. */
  setAside: number;
  status: number;
}

export interface ActorSyncGatewayFetchOptions {
  gatewayUrl: string;
  /** The head of the replica's journal right now. Read for every request, never cached here. */
  getJournalHead: () => ActorJournalHead;
  onAnswer?: (answer: ActorSyncGatewayAnswer) => void;
  /** An upload larger than this is sent in parts. Tests lower it. */
  partBytes?: number;
}

const TRAILING_SLASH = /\/$/;
const PIPELINE_PATH = /\/v[23]\/pipeline$/;
const REFUSALS: readonly ActorSyncGatewayRefusal[] = [
  "credential",
  "database-moved",
  "server-behind",
  "unavailable",
  "update-required",
];
/** Far below what the gateway accepts (8 MiB), so a part is cheap to send again. */
const DEFAULT_PART_BYTES = 512 * 1024;
/** However small its statements are, a part stays quick for the cloud copy to apply. */
const MAXIMUM_PART_STATEMENTS = 400;
const BEGIN_SQL = "BEGIN IMMEDIATE";
const COMMIT_SQL = "COMMIT";
const CURSOR_UPSERT_PREFIX = "INSERT INTO turso_sync_last_change_id(";

function readRefusal(value: string | null): ActorSyncGatewayRefusal | null {
  return REFUSALS.find((refusal) => refusal === value) ?? null;
}

function readAnswer(response: Response): ActorSyncGatewayAnswer {
  return {
    refusal: readRefusal(response.headers.get(ACTOR_SYNC_REFUSAL_HEADER)),
    setAside: Number(response.headers.get(ACTOR_SYNC_SET_ASIDE_HEADER) ?? 0) || 0,
    status: response.status,
  };
}

function readField(value: unknown, key: string): unknown {
  return typeof value === "object" && value !== null ? Reflect.get(value, key) : undefined;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

/**
 * The text of a request body the engine built, when it is large enough to be worth reading. The
 * engines hand over a string or bytes depending on the platform.
 */
function readLargeBodyText(body: unknown, partBytes: number): string | null {
  if (typeof body === "string") {
    return body.length > partBytes ? body : null;
  }
  const bytes =
    body instanceof ArrayBuffer
      ? new Uint8Array(body)
      : ArrayBuffer.isView(body)
        ? new Uint8Array(body.buffer, body.byteOffset, body.byteLength)
        : null;
  if (bytes === null || typeof TextDecoder === "undefined" || bytes.byteLength <= partBytes) {
    return null;
  }
  return new TextDecoder().decode(bytes);
}

function readStepSql(step: unknown): string | null {
  const sql = readField(readField(step, "stmt"), "sql");
  return typeof sql === "string" ? sql : null;
}

/**
 * Cut one upload into several, each a complete upload in the engine's own shape: the same opening
 * (`BEGIN`, the cursor table), a run of the statements in their order, the same cursor statement,
 * the same `COMMIT`. The gateway applies the parts in order, each exactly once, and lets only the
 * last one advance the cursor, so the engine sees one upload that succeeded.
 *
 * Returns `null` when `body` is small enough, or is not an upload (then it is sent as it is).
 */
export function splitActorSyncUpload(
  body: string,
  partBytes = DEFAULT_PART_BYTES,
): { parts: string[]; stepCount: number } | null {
  let payload: unknown;
  try {
    payload = JSON.parse(body);
  } catch {
    return null;
  }
  const requests = readField(payload, "requests");
  const request: unknown = Array.isArray(requests) && requests.length === 1 ? requests[0] : null;
  const steps = readField(readField(request, "batch"), "steps");
  if (!Array.isArray(steps) || steps.length < 5) {
    return null;
  }
  const opening: unknown[] = steps.slice(0, 2);
  const cursor: unknown = steps.at(-2);
  const commit: unknown = steps.at(-1);
  if (
    readStepSql(opening[0]) !== BEGIN_SQL ||
    readStepSql(commit) !== COMMIT_SQL ||
    readStepSql(cursor)?.startsWith(CURSOR_UPSERT_PREFIX) !== true
  ) {
    return null;
  }
  const changes: unknown[] = steps.slice(2, -2);
  const runs: unknown[][] = [];
  let run: unknown[] = [];
  let runBytes = 0;
  for (const change of changes) {
    const bytes = JSON.stringify(change).length;
    if (run.length > 0 && (runBytes + bytes > partBytes || run.length >= MAXIMUM_PART_STATEMENTS)) {
      runs.push(run);
      run = [];
      runBytes = 0;
    }
    run.push(change);
    runBytes += bytes;
  }
  runs.push(run);
  if (runs.length < 2) {
    return null;
  }
  return {
    parts: runs.map((part) =>
      JSON.stringify({
        ...asRecord(payload),
        requests: [
          {
            ...asRecord(request),
            batch: {
              ...asRecord(readField(request, "batch")),
              steps: [...opening, ...part, cursor, commit],
            },
          },
        ],
      }),
    ),
    stepCount: steps.length,
  };
}

/**
 * What the engine is told when every part of a split upload was applied: the answer to the last
 * part, stretched to as many steps as the engine sent.
 */
async function buildSplitUploadResponse(last: Response, stepCount: number): Promise<Response> {
  const payload: unknown = await last.json();
  const results = readField(payload, "results");
  const result = readField(
    readField(Array.isArray(results) ? results[0] : undefined, "response"),
    "result",
  );
  const stepResults = readField(result, "step_results");
  if (typeof result !== "object" || result === null || !Array.isArray(stepResults)) {
    throw new TypeError("The sync gateway answered an upload part in an unexpected shape");
  }
  const [stepResult] = stepResults;
  Reflect.set(result, "step_results", Array.from({ length: stepCount }).fill(stepResult));
  Reflect.set(result, "step_errors", Array.from({ length: stepCount }).fill(null));
  // oxlint-disable-next-line unicorn-js/prefer-response-static-json -- React Native's `Response` (whatwg-fetch) has no static `json`
  return new Response(JSON.stringify(payload), {
    headers: { "content-type": "application/json" },
    status: 200,
  });
}

/** One download request, headers and body: strictly inside the engine's 90s `waitChanges`. */
export const ACTOR_SYNC_DOWNLOAD_DEADLINE_MS = 45_000;

export function createActorSyncGatewayFetch({
  gatewayUrl,
  getJournalHead,
  onAnswer,
  partBytes,
}: ActorSyncGatewayFetchOptions): typeof fetch {
  return Object.assign(
    async (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
      const original = new URL(
        typeof input === "string" ? input : input instanceof URL ? input.href : input.url,
      );
      // The engine addresses the cloud copy; only the path and query are its own.
      const gateway = new URL(gatewayUrl);
      gateway.pathname = gateway.pathname.replace(TRAILING_SLASH, "") + original.pathname;
      gateway.search = original.search;
      const headers = new Headers(input instanceof Request ? input.headers : undefined);
      new Headers(init?.headers).forEach((value, name) => headers.set(name, value));
      headers.set(ACTOR_SYNC_PROTOCOL_HEADER, String(ACTOR_SYNC_PROTOCOL));

      const isPipeline = PIPELINE_PATH.test(gateway.pathname);
      const send = (body: RequestInit["body"], part: string | null): Promise<Response> => {
        const requestHeaders = new Headers(headers);
        // Read per request: a download applied between two requests can move the head.
        requestHeaders.set(ACTOR_SYNC_HEAD_HEADER, formatActorJournalHead(getJournalHead()));
        if (part !== null) {
          requestHeaders.set(ACTOR_SYNC_PART_HEADER, part);
        }
        return fetchWithDeadline(gateway, {
          ...init,
          body,
          // Both answers are read whole under the deadline: a pipeline's JSON, and a download's
          // binary pages, which the engine reads with one `arrayBuffer()`. Unbuffered, only the
          // headers were bounded, and a body that stalled hung the engine call until its own 90s
          // deadline stalled sync.
          // A download stays well inside the engine's 90s `waitChanges` bound, so a network hang
          // is an ordinary failed request, never an engine stall (`open-owned-turso-generation`).
          deadline: isPipeline ? 120_000 : ACTOR_SYNC_DOWNLOAD_DEADLINE_MS,
          headers: requestHeaders,
          operation: isPipeline ? "Cloud sync upload" : "Cloud sync download",
        });
      };

      const largeBody = isPipeline
        ? readLargeBodyText(init?.body, partBytes ?? DEFAULT_PART_BYTES)
        : null;
      const split = largeBody === null ? null : splitActorSyncUpload(largeBody, partBytes);
      if (split === null) {
        const response = await send(init?.body, null);
        onAnswer?.(readAnswer(response));
        return response;
      }
      // In order, each only after the one before it was applied. A part that fails ends the upload
      // with that answer; the engine sends it again later, and the gateway skips what is in.
      let setAside = 0;
      let last: Response | undefined;
      for (const [index, part] of split.parts.entries()) {
        last = await send(part, `${index}/${split.parts.length}`);
        const answer = readAnswer(last);
        if (!last.ok) {
          onAnswer?.(answer);
          return last;
        }
        setAside += answer.setAside;
      }
      if (last === undefined) {
        throw new Error("A split upload has no parts");
      }
      onAnswer?.({ refusal: null, setAside, status: 200 });
      return buildSplitUploadResponse(last, split.stepCount);
    },
    fetch,
  );
}
